import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Check, X, Clock, FileCheck, Eye, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  getKycSubmissions,
  updateKycStatus,
  KycData,
  formatDate,
} from '@/features/admin/services/adminService'

export default function AdminKyc() {
  const { t } = useTranslation()
  const [submissions, setSubmissions] = useState<KycData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState<string>('pending')
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [selectedKyc, setSelectedKyc] = useState<KycData | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  useEffect(() => {
    loadSubmissions()
  }, [filter])

  const loadSubmissions = async () => {
    setIsLoading(true)
    const data = await getKycSubmissions(filter === 'all' ? undefined : filter)
    setSubmissions(data)
    setIsLoading(false)
  }

  const handleApprove = async (kyc: KycData) => {
    setProcessingId(kyc.id)
    await updateKycStatus(kyc.id, 'approved')
    loadSubmissions()
    setProcessingId(null)
    setSelectedKyc(null)
  }

  const handleReject = async (kyc: KycData) => {
    if (!rejectReason) {
      alert('Ingresa un motivo de rechazo')
      return
    }
    setProcessingId(kyc.id)
    await updateKycStatus(kyc.id, 'rejected', rejectReason)
    loadSubmissions()
    setProcessingId(null)
    setSelectedKyc(null)
    setRejectReason('')
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="text-xs bg-yellow-900/50 text-yellow-400 px-2 py-1 rounded flex items-center gap-1"><Clock className="w-3 h-3" /> Pendiente</span>
      case 'approved':
        return <span className="text-xs bg-green-900/50 text-green-400 px-2 py-1 rounded flex items-center gap-1"><Check className="w-3 h-3" /> Aprobado</span>
      case 'rejected':
        return <span className="text-xs bg-red-900/50 text-red-400 px-2 py-1 rounded flex items-center gap-1"><X className="w-3 h-3" /> Rechazado</span>
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-cinzel text-2xl text-casino-gold">{t('admin.kyc')}</h1>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="pending">Pendientes</TabsTrigger>
          <TabsTrigger value="approved">Aprobados</TabsTrigger>
          <TabsTrigger value="rejected">Rechazados</TabsTrigger>
          <TabsTrigger value="all">Todos</TabsTrigger>
        </TabsList>

        <TabsContent value={filter} className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-casino-gold" />
            </div>
          ) : submissions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileCheck className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                <p className="text-gray-400">No hay verificaciones {filter === 'pending' ? 'pendientes' : ''}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {submissions.map((kyc) => (
                <Card key={kyc.id} className={kyc.status === 'pending' ? 'border-yellow-500/30' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-400" />
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <p className="font-medium">{kyc.fullName}</p>
                            {getStatusBadge(kyc.status)}
                          </div>
                          <p className="text-sm text-gray-400">
                            {kyc.documentType?.toUpperCase()}: {kyc.documentNumber}
                          </p>
                          <p className="text-xs text-gray-500">{formatDate(kyc.submittedAt)}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedKyc(kyc)}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Ver
                        </Button>
                        {kyc.status === 'pending' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleApprove(kyc)}
                              disabled={processingId === kyc.id}
                              className="border-green-600 text-green-500 hover:bg-green-900/20"
                            >
                              {processingId === kyc.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedKyc(kyc)}
                              className="border-red-600 text-red-500 hover:bg-red-900/20"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* KYC Detail Modal */}
      <Dialog open={!!selectedKyc} onOpenChange={() => setSelectedKyc(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Verificación KYC - {selectedKyc?.fullName}</DialogTitle>
          </DialogHeader>

          {selectedKyc && (
            <div className="space-y-4">
              {/* Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Documento</Label>
                  <p>{selectedKyc.documentType?.toUpperCase()}: {selectedKyc.documentNumber}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Estado</Label>
                  <div className="mt-1">{getStatusBadge(selectedKyc.status)}</div>
                </div>
              </div>

              {/* Documents */}
              <div className="space-y-4">
                <Label className="text-gray-500">Documentos</Label>
                <div className="grid grid-cols-3 gap-4">
                  {selectedKyc.docFront && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Frente</p>
                      <img
                        src={selectedKyc.docFront}
                        alt="Frente"
                        className="w-full h-32 object-cover rounded-lg border border-zinc-700"
                      />
                    </div>
                  )}
                  {selectedKyc.docBack && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Dorso</p>
                      <img
                        src={selectedKyc.docBack}
                        alt="Dorso"
                        className="w-full h-32 object-cover rounded-lg border border-zinc-700"
                      />
                    </div>
                  )}
                  {selectedKyc.selfie && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Selfie</p>
                      <img
                        src={selectedKyc.selfie}
                        alt="Selfie"
                        className="w-full h-32 object-cover rounded-lg border border-zinc-700"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              {selectedKyc.status === 'pending' && (
                <div className="space-y-4 pt-4 border-t border-zinc-700">
                  <div className="space-y-2">
                    <Label>Motivo de rechazo (opcional)</Label>
                    <Input
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Documento ilegible, foto borrosa, etc."
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="gold"
                      className="flex-1"
                      onClick={() => handleApprove(selectedKyc)}
                      disabled={processingId === selectedKyc.id}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Aprobar
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={() => handleReject(selectedKyc)}
                      disabled={processingId === selectedKyc.id || !rejectReason}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Rechazar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
